import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { useFilterStore } from '../store/filter-store';
import type { ChargeSpeedType } from '../types/filter.types';
import { BestPriceModal } from '@/features/best-price/components/BestPriceModal';
import { useOperatorStore } from '@/features/operator/store/operator-store';

interface ChipProps {
  active?: boolean;
  onClick: () => void;
  icon?: ReactNode;
  children: ReactNode;
  badge?: boolean;
}

function Chip({ active = false, onClick, icon, children, badge }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'relative inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-sm font-medium transition-colors',
        active
          ? 'bg-ink text-surface shadow-monta-md'
          : 'surface-float hover:bg-surface'
      )}
    >
      {icon}
      <span className="whitespace-nowrap">{children}</span>
      {badge && (
        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-monta-blue ring-2 ring-white" />
      )}
    </button>
  );
}

const BoltIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M13 2 4.5 13.5H11L10 22l8.5-11.5H12L13 2z" />
  </svg>
);

/**
 * One-tap filters floating over the map (Google/Apple Maps style). They drive
 * the same store toggles as the full FilterPanel, so URL sync and persistence
 * are unchanged; "Filters" opens the panel for everything else.
 */
export function QuickFilterChips({ wrap = false }: { wrap?: boolean }) {
  const { t } = useTranslation();
  const showOnlyAvailable = useFilterStore((s) => s.showOnlyAvailable);
  const setShowOnlyAvailable = useFilterStore((s) => s.setShowOnlyAvailable);
  const showSlow = useFilterStore((s) => s.showSlow);
  const showFast = useFilterStore((s) => s.showFast);
  const showUltraFast = useFilterStore((s) => s.showUltraFast);
  const toggleChargeSpeed = useFilterStore((s) => s.toggleChargeSpeed);
  const isFilterPanelOpen = useFilterStore((s) => s.isFilterPanelOpen);
  const toggleFilterPanel = useFilterStore((s) => s.toggleFilterPanel);
  const hasActiveFilters = useFilterStore((s) => s.hasActiveFilters());
  const showBestPriceButton = useFilterStore((s) => s.showBestPriceButton);
  const isBestPriceModalOpen = useFilterStore((s) => s.isBestPriceModalOpen);
  const setBestPriceModalOpen = useFilterStore((s) => s.setBestPriceModalOpen);
  const setAnchorPointVisible = useFilterStore((s) => s.setAnchorPointVisible);
  const themeColor = useOperatorStore((s) => s.theme?.primary_color);

  const isFastPlus = !showSlow && showFast && showUltraFast;
  const isUltraOnly = !showSlow && !showFast && showUltraFast;

  // Drive the existing per-speed toggles towards the wanted combination
  const setSpeeds = (wanted: Record<ChargeSpeedType, boolean>) => {
    const current = {
      slow: showSlow,
      fast: showFast,
      ultraFast: showUltraFast,
    };
    (Object.keys(wanted) as ChargeSpeedType[]).forEach((speed) => {
      if (current[speed] !== wanted[speed]) toggleChargeSpeed(speed);
    });
  };
  const allSpeeds = { slow: true, fast: true, ultraFast: true };

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-2',
          wrap ? 'flex-wrap' : 'no-scrollbar -mx-4 overflow-x-auto px-4 py-1'
        )}
      >
        <Chip
          active={isFilterPanelOpen}
          onClick={toggleFilterPanel}
          badge={hasActiveFilters && !isFilterPanelOpen}
          icon={
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0"
              />
              <circle cx="16" cy="6" r="2" />
              <circle cx="10" cy="12" r="2" />
              <circle cx="18" cy="18" r="2" />
            </svg>
          }
        >
          {t('filters.title')}
        </Chip>
        <Chip
          active={showOnlyAvailable}
          onClick={() => setShowOnlyAvailable(!showOnlyAvailable)}
          icon={
            <span
              className={cn(
                'h-2 w-2 rounded-full',
                showOnlyAvailable ? 'bg-green-400' : 'bg-green-500'
              )}
            />
          }
        >
          {t('filters.onlyAvailable')}
        </Chip>
        <Chip
          active={isFastPlus}
          onClick={() =>
            setSpeeds(
              isFastPlus
                ? allSpeeds
                : { slow: false, fast: true, ultraFast: true }
            )
          }
          icon={<BoltIcon />}
        >
          {t('filters.chips.fast')}
        </Chip>
        <Chip
          active={isUltraOnly}
          onClick={() =>
            setSpeeds(
              isUltraOnly
                ? allSpeeds
                : { slow: false, fast: false, ultraFast: true }
            )
          }
          icon={<BoltIcon />}
        >
          {t('filters.chips.ultraFast')}
        </Chip>
        {showBestPriceButton && (
          <Chip
            onClick={() => {
              setBestPriceModalOpen(true);
              setAnchorPointVisible(true);
            }}
            icon={
              <svg
                className="h-4 w-4"
                style={{ color: themeColor || '#302ee0' }}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 7h.01M7 3h5a1.99 1.99 0 0 1 1.41.59l7 7a2 2 0 0 1 0 2.82l-7 7a2 2 0 0 1-2.82 0l-7-7A2 2 0 0 1 3 12V7a4 4 0 0 1 4-4z"
                />
              </svg>
            }
          >
            {t('bestPrice.title')}
          </Chip>
        )}
      </div>

      <BestPriceModal
        isOpen={isBestPriceModalOpen}
        onClose={() => setBestPriceModalOpen(false)}
      />
    </>
  );
}
